import React, { useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { toast } from 'sonner';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { SocketProvider } from './contexts/SocketContext';
import { VaultProvider } from './contexts/VaultContext';
import Vault from './pages/Vault';
import Settings from './pages/Settings';
import Login from './pages/Login';
import Farewell from './pages/Farewell';
import Dashboard from './pages/Dashboard';
import Room from './pages/Room';
import Membership from './pages/Membership';
import Commons from './pages/Commons';
import Sworn from './pages/Sworn';
import SwornThread from './pages/SwornThread';
import SwornInvitation from './pages/SwornInvitation';
import Desk from './pages/staff/Desk';
import Members from './pages/staff/Members';
import Invites from './pages/staff/Invites';
import StaffMore from './pages/staff/StaffMore';
import StaffSection from './pages/staff/StaffSection';
import Onboarding from './pages/Onboarding';
import ResetPassword from './pages/ResetPassword';
import InstallPrompt from './components/InstallPrompt';
import AppLock from './components/AppLock';
import Decoy from './components/Decoy';
import { Toaster } from './components/ui/sonner';
import { homeFor, canReach } from './lib/navigation';

const ProtectedRoute = ({ children }) => {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-sm text-muted-foreground">Loading...</p>
        </div>
      </div>
    );
  }

  if (!user || user === false) {
    return <Navigate to="/login" replace />;
  }

  return children;
};

const PublicRoute = ({ children }) => {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-sm text-muted-foreground">Loading...</p>
        </div>
      </div>
    );
  }

  if (user && user !== false) {
    return <Navigate to={homeFor(user)} replace />;
  }

  return children;
};

/**
 * Staff destinations. Signed in, AND offered by this user's navigation: a
 * typed URL gets the same doors the menus show, and anything else goes home.
 * Cosmetic by design -- every API behind these pages checks the role itself.
 */
const StaffRoute = ({ children }) => {
  const { user, loading } = useAuth();
  const { pathname } = useLocation();
  if (loading) return null;
  if (!user || user === false) return <Navigate to="/login" replace />;
  if (!canReach(user, pathname)) return <Navigate to={homeFor(user)} replace />;
  return children;
};

/** "/" and anything signed-in without a destination: the user's own landing. */
const Home = () => {
  const { user, loading } = useAuth();
  if (loading) return null;
  if (!user || user === false) return <Navigate to="/login" replace />;
  return <Navigate to={homeFor(user)} replace />;
};

function App() {
  // Shown when the service worker installed a new build behind this page
  // (event dispatched from the registration code in public/index.html).
  useEffect(() => {
    const onSwUpdate = () => {
      toast('A new version of Arcanum is available', {
        duration: Infinity,
        action: {
          label: 'Refresh',
          onClick: () => window.location.reload(),
        },
      });
    };
    window.addEventListener('arca-sw-updated', onSwUpdate);
    return () => window.removeEventListener('arca-sw-updated', onSwUpdate);
  }, []);

  return (
    // Outside every provider on purpose. While the disguise is up nothing of
    // Arcanum is mounted -- no session lookup, no socket, no requests. Wrapping
    // further in would leave the real app running behind a picture.
    <Decoy>
      <AuthProvider>
        <SocketProvider>
          <VaultProvider>
          <BrowserRouter>
            <Routes>
              <Route
                path="/login"
                element={
                  <PublicRoute>
                    <Login />
                  </PublicRoute>
                }
              />
              <Route path="/onboard/:token" element={<Onboarding />} />
              <Route path="/reset/:token" element={<ResetPassword />} />
              {/* Unguarded on purpose: signing out flips the user to false,
                  which would bounce a guarded route to /login instantly. */}
              <Route path="/farewell" element={<Farewell />} />
              {/* Where a member arrives. /dashboard stays their Support. */}
              <Route
                path="/room"
                element={
                  <ProtectedRoute>
                    <Room />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/membership"
                element={
                  <ProtectedRoute>
                    <Membership />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/dashboard"
                element={
                  <ProtectedRoute>
                    <Dashboard />
                  </ProtectedRoute>
                }
              />
              {/* The old single admin panel, now five destinations. /admin
                  itself sends each role to its own landing. */}
              <Route path="/admin" element={<Home />} />
              <Route path="/admin/desk" element={<StaffRoute><Desk /></StaffRoute>} />
              <Route path="/admin/members" element={<StaffRoute><Members /></StaffRoute>} />
              <Route path="/admin/invites" element={<StaffRoute><Invites /></StaffRoute>} />
              <Route path="/admin/more" element={<StaffRoute><StaffMore /></StaffRoute>} />
              {/* StaffSection checks its own section against the role, so
                  an operator's typed /admin/more/operators goes back to More. */}
              <Route path="/admin/more/:section" element={<StaffRoute><StaffSection /></StaffRoute>} />
              <Route
                path="/commons"
                element={
                  <ProtectedRoute>
                    <Commons />
                  </ProtectedRoute>
                }
              />
              {/* Guarded twice over, and neither is the interesting one. The
                  route is signed-in only; the API answers 404 to anybody not
                  in the circle, and the page leaves when it gets one. There is
                  no "you are not a member" screen anywhere, because such a
                  screen would tell somebody the circle exists. */}
              <Route
                path="/sworn"
                element={
                  <ProtectedRoute>
                    <Sworn />
                  </ProtectedRoute>
                }
              />
              {/* A private thread between two of the circle. Reachable only by
                  tapping somebody in the room's member list -- there is no
                  other route to it anywhere in the application, which is what
                  keeps member-to-member messaging a room rather than a
                  capability (docs/sworn_design.md §2.1). Knowing the URL buys
                  nothing: the API answers 404 to anybody who is not one of the
                  two parties. */}
              {/* Being asked in. NOT gated on being sworn -- by definition the
                  person reading it is not, yet. It answers 404 to anybody
                  without a live offer, which is what every other Sworn route
                  answers to an outsider, so the two look the same from
                  outside (docs/sworn_design.md §7, §3.1). */}
              <Route
                path="/sworn/invitation"
                element={
                  <ProtectedRoute>
                    <SwornInvitation />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/sworn/thread/:threadId"
                element={
                  <ProtectedRoute>
                    <SwornThread />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/vault"
                element={
                  <ProtectedRoute>
                    <Vault />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/settings"
                element={
                  <ProtectedRoute>
                    <Settings />
                  </ProtectedRoute>
                }
              />
              <Route path="/" element={<Home />} />
            </Routes>
            <InstallPrompt />
            {/* Sits above the routes so it covers whatever is open. */}
            <AppLock />
            <Toaster />
          </BrowserRouter>
          </VaultProvider>
        </SocketProvider>
      </AuthProvider>
    </Decoy>
  );
}

export default App;
